import { Button, Flex, Pagination, Stack } from '@mantine/core'
import { useInterval } from '@mantine/hooks'
import { IconRefresh } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useGetVlmResultList } from '@/queries/vlmQueries'
import { VlmResult } from '@/types/vlm'
import { getPageCount, getPageOffset } from '@/utils/pagination'
import { useTableState } from '../ui-shared/Tables/useTableState'
import { VlmResultDrawer } from './VlmResultDrawer'
import { VlmResultList } from './VlmResultList'
import { VlmResultsFilters } from './VlmResultsFilters'
import { DEFAULT_FILTERS, getStartDate } from './vlmResultsFilterConfig'

const PAGE_SIZE = 10
const POLL_INTERVAL = 20000

export const VlmResultsPage = () => {
  const [tableState, setTableState] = useTableState({
    activePage: 1,
    filterConfig: DEFAULT_FILTERS
  })
  const { activePage, filterConfig } = tableState
  const [selectedResult, setSelectedResult] = useState<VlmResult | null>(null)

  const filters = {
    ...DEFAULT_FILTERS,
    ...filterConfig
  }

  const startDate = useMemo(
    () => getStartDate(filters.dateRange),
    [filters.dateRange]
  )

  const { data, isLoading, isFetching, isError, refetch } = useGetVlmResultList(
    {
      offset: getPageOffset(activePage, PAGE_SIZE),
      limit: PAGE_SIZE,
      start_date: startDate
    },
    { keepPreviousData: true }
  )

  const results = data?.results ?? []
  const totalPageCount = getPageCount(data?.count ?? 0, PAGE_SIZE)

  const { start, stop, active } = useInterval(
    () => void refetch(),
    POLL_INTERVAL
  )

  useEffect(() => {
    if (data && !active) {
      start()
    }
  }, [data, active, start])

  useEffect(() => {
    return stop
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <PlatformAlert
        variant="info"
        title={<FormattedMessage id="vlm.results.comingSoon.title" />}
        mb="md"
      >
        <FormattedMessage id="vlm.results.comingSoon.description" />
      </PlatformAlert>

      <Flex justify="space-between" align="center" gap="sm" mb="sm">
        <VlmResultsFilters
          filters={filters}
          onFiltersChange={(newFilters) =>
            setTableState({ activePage: 1, filterConfig: newFilters })
          }
        />
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconRefresh size={14} />}
          loading={isFetching}
          onClick={() => void refetch()}
        >
          <FormattedMessage id="refresh" />
        </Button>
      </Flex>

      <Stack>
        <VlmResultList
          results={results}
          isLoading={isLoading}
          hasError={isError}
          onReload={() => void refetch()}
          onResultClick={(result) => setSelectedResult(result)}
        />

        <Flex justify="end">
          <Pagination
            size="sm"
            total={totalPageCount}
            value={activePage}
            onChange={(value) => setTableState({ activePage: value })}
          />
        </Flex>
      </Stack>

      <VlmResultDrawer
        result={selectedResult}
        onClose={() => setSelectedResult(null)}
      />
    </>
  )
}
